import { formatCurrency } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Circle, Plus, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const formatDate = (d) => { try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; } };
const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
const toInput = (v) => (v === null || v === undefined ? '' : Number(v).toFixed(2));

const SummaryLine = ({ label, value, className = '', testId }) => (
  <div className="flex justify-between text-sm py-0.5">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono ${className}`} data-testid={testId}>{value}</span>
  </div>
);

const CheckItem = ({ ok, label, testId }) => (
  <div className={`flex items-center gap-2 text-sm ${ok ? 'text-green-700' : 'text-muted-foreground'}`} data-testid={testId}>
    {ok ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
    {label}
  </div>
);

const emptyMovement = (date) => ({ expense_date: date, movement_type: 'cash_out', amount: '', supplier_reason: '' });

const AddMovementForm = ({ period, onAdded }) => {
  const { api } = useAuth();
  const [m, setM] = useState(emptyMovement(period.end_date));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setM(prev => ({ ...prev, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    if (!m.amount || !m.supplier_reason.trim()) { toast.error('Enter an amount and a reason'); return; }
    if (m.expense_date < period.start_date || m.expense_date > period.end_date) { toast.error('Date must be inside this period'); return; }
    setSaving(true);
    try {
      const res = await api.post('/cash-expenses', { ...m, business_id: period.business_id, amount: round2(m.amount) });
      try {
        await api.post(`/cash-expenses/${res.data.id}/confirm`);
        toast.success('Cash movement added');
      } catch {
        toast.warning('Movement added but not confirmed. Confirm it on the Cash Movement page.');
      }
      setM(emptyMovement(m.expense_date));
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add movement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="recon-add-movement">
      <div><Label className="text-xs">Date</Label><Input type="date" value={m.expense_date} min={period.start_date} max={period.end_date} onChange={set('expense_date')} className="w-36 h-8" data-testid="recon-movement-date" /></div>
      <div>
        <Label className="text-xs">Type</Label>
        <Select value={m.movement_type} onValueChange={set('movement_type')}>
          <SelectTrigger className="w-28 h-8" data-testid="recon-movement-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cash_in">Cash In</SelectItem>
            <SelectItem value="cash_out">Cash Out</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" placeholder="0.00" value={m.amount} onChange={set('amount')} className="w-28 h-8" data-testid="recon-movement-amount" /></div>
      <div className="flex-1 min-w-[180px]"><Label className="text-xs">Supplier / reason</Label><Input value={m.supplier_reason} onChange={set('supplier_reason')} className="h-8" data-testid="recon-movement-reason" /></div>
      <Button variant="outline" size="sm" className="h-8" onClick={submit} disabled={saving} data-testid="recon-movement-submit">
        <Plus className="h-4 w-4 mr-1" />{saving ? 'Adding...' : 'Add Movement'}
      </Button>
    </div>
  );
};

const EntriesTable = ({ entries, readOnly, onTick }) => {
  const allTicked = entries.length > 0 && entries.every(e => e.ticked);
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#F2F2F0]">
            <TableHead className="w-8 h-9 px-2">
              <Checkbox className="border-[#006241] data-[state=checked]:bg-[#006241]" checked={allTicked} disabled={readOnly || !entries.length} onCheckedChange={(v) => onTick(entries.map(e => e.id), !!v)} data-testid="recon-tick-all" />
            </TableHead>
            <TableHead className="h-9 px-2">Date</TableHead>
            <TableHead className="h-9 px-2">Type</TableHead>
            <TableHead className="h-9 px-2">Description</TableHead>
            <TableHead className="h-9 px-2 text-right">Amount</TableHead>
            <TableHead className="h-9 px-2 text-right">Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No ledger entries in this period</TableCell></TableRow>
          ) : entries.map(e => (
            <TableRow key={e.id} className={e.ticked ? 'bg-green-50/60' : ''} data-testid={`recon-entry-${e.id}`}>
              <TableCell className="px-2 py-1.5">
                <Checkbox className="border-[#006241] data-[state=checked]:bg-[#006241]" checked={e.ticked} disabled={readOnly} onCheckedChange={(v) => onTick([e.id], !!v)} data-testid={`recon-tick-${e.id}`} />
              </TableCell>
              <TableCell className="px-2 py-1.5 whitespace-nowrap text-sm">{formatDate(e.entry_date)}</TableCell>
              <TableCell className="px-2 py-1.5 capitalize text-sm whitespace-nowrap">{e.entry_type.replace('_', ' ')}</TableCell>
              <TableCell className="px-2 py-1.5 text-sm text-muted-foreground max-w-0 w-full truncate" title={e.description}>{e.description}</TableCell>
              <TableCell className={`px-2 py-1.5 text-right font-mono text-sm whitespace-nowrap ${e.amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>{e.amount >= 0 ? '+' : ''}{formatCurrency(e.amount)}</TableCell>
              <TableCell className="px-2 py-1.5 text-right font-mono text-sm whitespace-nowrap">{formatCurrency(e.running_balance)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const BalanceInput = ({ label, value, onChange, onBlur, system, disabled, testId }) => {
  const has = value !== '';
  const diff = round2(round2(value) - system);
  return (
    <div>
      <Label>{label}</Label>
      <Input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} disabled={disabled} placeholder="0.00" className="mt-1 h-8 font-mono" data-testid={testId} />
      {has && (
        <p className={`text-xs mt-1 font-medium ${diff === 0 ? 'text-green-700' : 'text-red-700'}`} data-testid={`${testId}-diff`}>
          {diff === 0 ? 'Matches system' : `Difference: ${formatCurrency(diff)}`}
        </p>
      )}
    </div>
  );
};

const BalancesPanel = ({ period, accounting, setAccounting, counted, setCounted, saveDraft, onReconcile, saving }) => {
  const s = period.status === 'reconciled' && period.reconciled_snapshot ? period.reconciled_snapshot : period.current;
  const readOnly = period.status === 'reconciled';
  const system = s.closing_balance;
  const accOk = accounting !== '' && round2(accounting) === system;
  const cashOk = counted !== '' && round2(counted) === system;
  const ticked = period.entries.filter(e => e.ticked).length;
  const allTicked = ticked === period.entries.length;
  const openingMismatch = period.expected_opening_balance != null && round2(period.expected_opening_balance) !== round2(s.opening_balance);

  return (
    <Card className="lg:sticky lg:top-4" data-testid="recon-balances">
      <CardHeader className="px-4 py-3"><CardTitle className="text-sm">Balances</CardTitle></CardHeader>
      <CardContent className="space-y-3 px-4 pb-4 pt-0">
        <div className="rounded-lg bg-[#F2F2F0] px-3 py-2">
          <SummaryLine label="Opening balance" value={formatCurrency(s.opening_balance)} testId="recon-opening" />
          <SummaryLine label="Cash in" value={`+${formatCurrency(s.total_in)}`} className="text-green-700" />
          <SummaryLine label="Cash out" value={s.total_out ? `-${formatCurrency(s.total_out)}` : formatCurrency(0)} className="text-red-700" />
          <SummaryLine label="System closing balance" value={formatCurrency(system)} className="font-semibold" testId="recon-system-closing" />
        </div>
        {openingMismatch && (
          <div className="flex gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-3" data-testid="recon-opening-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Opening balance differs from the previous period's ending balance ({formatCurrency(period.expected_opening_balance)}).
          </div>
        )}
        <BalanceInput label="Ending balance in Xero / Zoho" value={accounting} onChange={setAccounting} onBlur={saveDraft} system={system} disabled={readOnly} testId="recon-accounting-input" />
        <BalanceInput label="Cash counted" value={counted} onChange={setCounted} onBlur={saveDraft} system={system} disabled={readOnly} testId="recon-cash-input" />
        <div className="space-y-1.5 border-t pt-3">
          <CheckItem ok={accOk} label="Xero / Zoho matches system" testId="recon-check-accounting" />
          <CheckItem ok={cashOk} label="Cash counted matches system" testId="recon-check-cash" />
          <CheckItem ok={allTicked} label={`All lines ticked (${ticked} / ${period.entries.length})`} testId="recon-check-ticks" />
        </div>
        {!readOnly && (
          <Button onClick={onReconcile} disabled={!(accOk && cashOk && allTicked) || saving} className="w-full bg-[#006241] hover:bg-[#004d33]" data-testid="recon-submit">
            <CheckCircle2 className="h-4 w-4 mr-1" />{saving ? 'Reconciling...' : 'Mark as Reconciled'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default function PeriodReconPage() {
  const { periodId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [period, setPeriod] = useState(null);
  const [accounting, setAccounting] = useState('');
  const [counted, setCounted] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (resetInputs = false) => {
    try {
      const res = await api.get(`/periods/${periodId}`);
      setPeriod(res.data);
      if (resetInputs) {
        setAccounting(toInput(res.data.accounting_ending_balance));
        setCounted(toInput(res.data.cash_counted));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load period');
    }
  }, [api, periodId]);

  useEffect(() => { load(true); }, [load]);

  const saveDraft = async () => {
    if (!period || period.status !== 'open') return;
    try {
      await api.put(`/periods/${periodId}/draft`, {
        accounting_ending_balance: accounting === '' ? null : round2(accounting),
        cash_counted: counted === '' ? null : round2(counted)
      });
    } catch {
      toast.error('Failed to save balances');
    }
  };

  const handleTick = async (ids, ticked) => {
    setPeriod(prev => ({ ...prev, entries: prev.entries.map(e => (ids.includes(e.id) ? { ...e, ticked } : e)) }));
    try {
      await api.post(`/periods/${periodId}/tick`, { entry_ids: ids, ticked });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save tick');
      load();
    }
  };

  const handleReconcile = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/periods/${periodId}/reconcile`, { accounting_ending_balance: round2(accounting), cash_counted: round2(counted) });
      toast.success(res.data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reconcile');
    } finally {
      setSaving(false);
    }
  };

  if (!period) {
    return <div className="page-container"><Skeleton className="h-10 w-64 mb-6" /><Skeleton className="h-96" /></div>;
  }

  const reconciled = period.status === 'reconciled';

  return (
    <div className="px-4 py-4 md:px-6 animate-slide-in" data-testid="period-recon-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/periods')} className="mb-1 -ml-2 h-7" data-testid="recon-back-btn">
        <ArrowLeft className="h-4 w-4 mr-1" />Recon Periods
      </Button>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
        <h1 className="text-xl font-semibold text-[#1A1A18]">Reconcile {period.name}</h1>
        {reconciled ? (
          <Badge className="bg-[#006241] text-white" data-testid="recon-status"><CheckCircle2 className="h-3 w-3 mr-1" />Reconciled</Badge>
        ) : (
          <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50" data-testid="recon-status">Open</Badge>
        )}
        <p className="text-muted-foreground text-sm w-full">
          {period.business_name} · {formatDate(period.start_date)} to {formatDate(period.end_date)}
          {reconciled && ` · reconciled by ${period.reconciled_by}`}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_300px] items-start">
        <div className="space-y-3 min-w-0">
          {!reconciled && (
            <Card>
              <CardHeader className="px-4 py-3"><CardTitle className="text-sm">Add a cash movement</CardTitle></CardHeader>
              <CardContent className="px-4 pb-3 pt-0"><AddMovementForm period={period} onAdded={() => load()} /></CardContent>
            </Card>
          )}
          <Card data-testid="recon-entries">
            <CardHeader className="px-4 py-3"><CardTitle className="text-sm">Line items</CardTitle></CardHeader>
            <CardContent className="p-0">
              <EntriesTable entries={period.entries} readOnly={reconciled} onTick={handleTick} />
            </CardContent>
          </Card>
        </div>
        <BalancesPanel
          period={period}
          accounting={accounting}
          setAccounting={setAccounting}
          counted={counted}
          setCounted={setCounted}
          saveDraft={saveDraft}
          onReconcile={handleReconcile}
          saving={saving}
        />
      </div>
    </div>
  );
}
